import { ClientWorkspacePlanNid, IndependentDiscoverabilityFactorEducationDrawer, OnboardingSources, UserTypeRouteBase, } from './schemas.js';
import { DiscoverExpertsRouteBase } from '@contra/expert-partnerships';
import { z } from 'zod';
export const routes = {
    '*': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            addPayoutMethod: z.boolean().optional().default(false),
            // This parameter controls whethere we render the external URL input inside the job creation
            // modal. This parameter is defined here because the modal can be opened on any page.
            communityJobPosting: z.boolean().optional().default(false),
            contraProGuide: z.boolean().optional().default(false),
            debitCard: z.boolean().optional().default(false),
            debugGrid: z.boolean().optional().default(false),
            emailsToAssociate: z.array(z.string()).optional(),
            emailsToCreate: z.array(z.string()).optional(),
            fromPayoneerOnboarding: z.boolean().optional().default(false),
            fromStripeOnboarding: z.boolean().optional().default(false),
            justRedeemedProCode: z.boolean().optional().default(false),
            justSignedUp: z.boolean().optional().default(false),
            justSubscribedToProModalOpen: z.boolean().optional().default(false),
            message: z.string().optional(),
            // used to open a specific GlobalModalSystem modal on navigation
            modalName: z
                .enum([
                'JobCreationModal',
                'AnonymousJobCreationModal',
                'AddContactModal',
                'IndyPortfolioSubscriptionModal',
            ])
                .optional(),
            notificationId: z.string().optional(),
            'onboarding-completed': z.boolean().optional().default(false),
            // used for useDiscoverSearchQueryParameters
            outcomes: z.array(z.string()).optional(),
            page: z.number().optional(),
            paypalCode: z.string().optional(),
            pid: z.string().optional(),
            profileChecklist: z.boolean().optional().default(false),
            profileChecklistAnimation: z.boolean().optional().default(false),
            profileMagicTestError: z
                .enum(['RATE_LIMIT', 'USER_RATE_LIMIT', 'OTHER'])
                .optional(),
            // used for useDiscoverSearchQueryParameters
            query: z.string().optional(),
            redeemContraProCode: z.string().optional(),
            redirectTo: z.string().optional(),
            referralIntentId: z.string().optional(),
            refresh: z.boolean().optional().default(false),
            refreshStripe: z.boolean().optional().default(false),
            showRevealAnimation: z.boolean().optional().default(false),
            sortBy: z.string().optional(),
            startInquiry: z.boolean().optional().default(false),
            upgradeToPro: z.boolean().optional().default(false),
            userType: UserTypeRouteBase.optional(),
            utm_campaign: z.string().optional(),
            utm_medium: z.string().optional(),
            utm_source: z.string().optional(),
            viewAsPublic: z.boolean().optional().default(false),
        })
            .optional(),
    }),
    '/': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            addBio: z.boolean().optional().default(false),
            closePanelOnMount: z.boolean().optional().default(false),
            shouldOpenAddLinks: z.boolean().optional().default(false),
        })
            .optional(),
    }),
    '/@expert': z.object({
        args: z.object({ expert: DiscoverExpertsRouteBase }),
        search: z
            .object({
            languages: z.array(z.string()).optional(),
            preview: z.string().optional(),
            userAccountIds: z.array(z.string()).optional(),
        })
            .optional(),
    }),
    '/@internal/admin': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            search: z.string().optional(),
        })
            .optional(),
    }),
    '/@internal/community/tags': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/@internal/debug': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/@internal/discovery/@username': z.object({
        args: z.object({
            username: z.string(),
        }),
        search: z
            .object({
            education: IndependentDiscoverabilityFactorEducationDrawer.optional(),
            skill: z.string().optional(),
            tool: z.string().optional(),
        })
            .optional(),
    }),
    '/@internal/errors': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            pageMountError: z.boolean().optional().default(false),
        })
            .optional(),
    }),
    '/@internal/open-graph-card/opportunity/@slug': z.object({
        args: z.object({
            slug: z.string(),
        }),
        search: z
            .object({
            utm_source: z.string().optional(),
        })
            .optional(),
    }),
    '/@internal/open-graph-card/project/@id': z.object({
        args: z.object({
            id: z.string(),
        }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/@internal/open-graph-card/service/@slug': z.object({
        args: z.object({
            slug: z.string(),
        }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/@internal/open-graph-card/user/@username': z.object({
        args: z.object({
            username: z.string(),
        }),
        search: z
            .object({
            force_test_og_image: z.boolean().optional(),
            utm_source: z.string().optional(),
        })
            .optional(),
    }),
    '/@internal/opportunity/new': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/@internal/sales-demo-setup': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/@internal/test-relay-subscriptions': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/@internal/test-sentry': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/@username': z.object({
        args: z.object({ username: z.string() }),
        search: z
            .object({
            addBio: z.boolean().optional().default(false),
            addHeadline: z.boolean().optional().default(false),
            addToList: z.boolean().optional().default(false),
            closePanelOnMount: z.boolean().optional().default(false),
            editFromPortfolio: z.boolean().optional().default(false),
            inquiry: z.boolean().optional().default(false),
            profileMagic: z.boolean().optional().default(false),
            redirectBackTo: z.string().optional(),
            shareProfile: z.boolean().optional().default(false),
            startInquiry: z.boolean().optional().default(false),
            updateAvailability: z.boolean().optional().default(false),
        })
            .optional(),
    }),
    '/@username/opportunities': z.object({
        args: z.object({ username: z.string() }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/@username/projects': z.object({
        args: z.object({ username: z.string() }),
        search: z
            .object({
            addBio: z.boolean().optional().default(false),
            profileMagic: z.boolean().optional().default(false),
            redirectToPortfolio: z.boolean().optional().default(false),
            shouldOpenAddLinks: z.boolean().optional().default(false),
        })
            .optional(),
    }),
    '/@username/recommendations': z.object({
        args: z.object({ username: z.string() }),
        search: z
            .object({
            redirectToPortfolio: z.boolean().optional().default(false),
        })
            .optional(),
    }),
    '/@username/services': z.object({
        args: z.object({ username: z.string() }),
        search: z
            .object({
            profileMagic: z.boolean().optional().default(false),
            redirectToPortfolio: z.boolean().optional().default(false),
        })
            .optional(),
    }),
    '/@userType/messages': z.object({
        args: z.object({ userType: UserTypeRouteBase.optional() }),
        search: z
            .object({
            channelId: z.string().optional(),
        })
            .optional(),
    }),
    '/@userType/projects': z.object({
        args: z.object({ userType: UserTypeRouteBase }),
        search: z
            .object({
            drafts: z.boolean().optional().default(false),
            independents: z.array(z.string()).optional(),
            openFiltersModal: z.boolean().optional(),
            organizations: z.array(z.string()).optional(),
            owners: z.array(z.string()).optional(),
            page: z.number().optional().default(1),
            sortBy: z.enum(['CREATED_AT_DESC', 'CREATED_AT_ASC']).optional(),
            statuses: z
                .array(z.enum([
                'PROPOSAL',
                'PROPOSAL_DECLINED',
                'ACTIVE',
                'ON_HOLD',
                'COMPLETED',
                'CANCELLED',
            ]))
                .optional(),
            tab: z.enum(['active', 'completed']).optional(),
        })
            .optional(),
    }),
    '/@userType/projects/@paidProjectId': z.object({
        args: z.object({
            paidProjectId: z.string(),
            userType: UserTypeRouteBase,
        }),
        search: z
            .object({
            showUnlockedIndyJourneyModal: z.boolean().optional(),
            tab: z.enum(['invoices', 'scope']).optional(),
        })
            .optional(),
    }),
    '/@userType/projects/@paidProjectId/confirm-payment': z.object({
        args: z.object({
            paidProjectId: z.string(),
            userType: UserTypeRouteBase,
        }),
        search: z
            .object({
            query: z.string().optional(),
        })
            .optional(),
    }),
    '/@userType/projects/@paidProjectId/duplicate': z.object({
        args: z.object({
            paidProjectId: z.string(),
            userType: UserTypeRouteBase.optional(),
        }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/@userType/projects/@paidProjectId/edit': z.object({
        args: z.object({
            paidProjectId: z.string(),
            userType: UserTypeRouteBase,
        }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/@userType/projects/@paidProjectId/invoices/@invoiceId': z.object({
        args: z.object({
            invoiceId: z.string(),
            paidProjectId: z.string(),
            userType: UserTypeRouteBase,
        }),
        search: z.object({
            paymentSuccess: z.boolean().optional().default(false),
        }),
    }),
    '/@userType/projects/@paidProjectId/invoices/@invoiceId/pay': z.object({
        args: z.object({
            invoiceId: z.string(),
            paidProjectId: z.string(),
            userType: UserTypeRouteBase,
        }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/@userType/projects/@paidProjectId/review': z.object({
        args: z.object({
            paidProjectId: z.string(),
            userType: UserTypeRouteBase,
        }),
        search: z
            .object({
            needsEducation: z.boolean().default(false).optional(),
        })
            .optional(),
    }),
    '/@userType/wallet': z.object({
        args: z.object({
            userType: UserTypeRouteBase,
        }),
        search: z
            .object({
            addPayoutMethod: z.boolean().optional().default(false),
            code: z.string().optional(),
            debitCard: z.boolean().optional().default(false),
            endDate: z.string().optional(),
            fromPayoneerOnboarding: z.boolean().optional().default(false),
            fromStripeOnboarding: z.boolean().optional().default(false),
            intervalNumber: z.number().optional(),
            networkInviteSlug: z.string().optional(),
            page: z.number().optional(),
            paidProjectIds: z.array(z.string()).optional(),
            projectId: z.string().optional(),
            projectTitle: z.string().optional(),
            rangeInterval: z.enum(['DAY', 'WEEK', 'MONTH', 'YEAR']).optional(),
            rangeType: z
                .enum([
                'IN_THE_LAST_N_INTERVAL',
                'EQUAL_TO_DATE',
                'BETWEEN_DATE_AND_DATE',
                'ON_OR_AFTER_DATE',
                'BEFORE_DATE',
            ])
                .optional(),
            scope: z.array(z.string()).optional(),
            search: z.string().optional(),
            sortBy: z.enum(['DESC', 'ASC', 'RELEVANCE']).optional(),
            startDate: z.string().optional(),
            statuses: z.array(z.string()).optional(),
        })
            .optional(),
    }),
    '/account-deleted': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/almost-there': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            jobPostingId: z.string().optional(),
        })
            .optional(),
    }),
    '/auth/confirm': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            code: z.string().optional(),
            emailAddress: z.string().email().optional(),
            impersonateUser: z.boolean().optional().default(false),
            redirectTo: z.string().optional(),
        })
            .optional(),
    }),
    '/auth/custom-domain': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            isInternalTraffic: z.boolean().optional().default(false),
            upgradeNow: z.boolean().optional().default(false),
            username: z.string().optional(),
            utm_campaign: z.string().optional(),
            utm_medium: z.string().optional(),
            utm_source: z.string().optional(),
        })
            .optional(),
    }),
    '/book-a-meeting': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            email: z.string().optional(),
            firstName: z.string().optional(),
            lastName: z.string().optional(),
            referralIntentId: z.string().optional(),
            source: z.string().optional(),
            uuid: z.string().optional(),
        })
            .optional(),
    }),
    '/client/home': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            modalName: z.enum(['JobCreationModal']).optional(),
            viewAs: z.string().optional(),
        })
            .optional(),
    }),
    '/client/invoices': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/client/leave-recommendation/@id': z.object({
        args: z.object({ id: z.string() }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/client/leave-recommendation/invite/@token': z.object({
        args: z.object({ token: z.string() }),
        search: z.object({
            recommendationRequestId: z.string(),
        }),
    }),
    '/client/opportunities': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            'first-opportunity-created-success': z
                .boolean()
                .optional()
                .default(false),
            openFiltersModal: z.boolean().optional(),
            owners: z.array(z.string()).optional(),
            page: z.number().optional().default(1),
            sortBy: z.enum(['CREATED_AT_DESC', 'CREATED_AT_ASC']).optional(),
            statuses: z
                .array(z.enum([
                'CLOSED',
                'IN_REVIEW',
                'PENDING_REVISION',
                'POSTED',
                'PRIVATE',
                'REJECTED',
                'EXPIRED',
            ]))
                .optional(),
        })
            .optional(),
    }),
    '/client/projects/@paidProjectId/invoices/@invoiceId/pay': z.object({
        args: z.object({
            invoiceId: z.string(),
            paidProjectId: z.string(),
        }),
        search: z
            .object({
            token: z.string().optional(),
        })
            .optional(),
    }),
    '/community': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            joinWaitlist: z.boolean().optional(),
            scrollTo: z.string().optional(),
            username: z.string().optional(),
        })
            .optional(),
    }),
    '/community/@slug': z.object({
        args: z.object({
            slug: z.string(),
        }),
        search: z
            .object({
            referralExperimentNid: z.string().optional(),
            referrerUsername: z.string().optional(),
            replyId: z.string().optional(),
        })
            .optional(),
    }),
    '/community/bookmarks': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/community/notifications': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/community/profile/@username': z.object({
        args: z.object({
            username: z.string(),
        }),
        search: z.object({
            joinWaitlist: z.boolean().optional(),
            referralExperimentNid: z.string().optional(),
            referrerUsername: z.string().optional(),
            scrollTo: z.string().optional(),
        }),
    }),
    '/community/recent': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            joinWaitlist: z.boolean().optional(),
            referralExperimentNid: z.string().optional(),
            referrerUsername: z.string().optional(),
            scrollTo: z.string().optional(),
            username: z.string().optional(),
        })
            .optional(),
    }),
    '/community/topic/@slug': z.object({
        args: z.object({
            slug: z.string(),
        }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/community/topic/@slug/recent': z.object({
        args: z.object({
            slug: z.string(),
        }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/community/topic/@slug/trending': z.object({
        args: z.object({
            slug: z.string(),
        }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/community/trending': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            joinWaitlist: z.boolean().optional(),
            scrollTo: z.string().optional(),
            username: z.string().optional(),
        })
            .optional(),
    }),
    '/company/@slug': z.object({
        args: z.object({
            slug: z.string(),
        }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/company/@slug/content': z.object({
        args: z.object({
            slug: z.string(),
        }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/company/@slug/jobs': z.object({
        args: z.object({
            slug: z.string(),
        }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/company/@slug/lists': z.object({
        args: z.object({
            slug: z.string(),
        }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/company/@slug/people': z.object({
        args: z.object({
            slug: z.string(),
        }),
        search: z
            .object({
            member: z.string().optional(),
        })
            .optional(),
    }),
    '/company/@slug/recommendations': z.object({
        args: z.object({
            slug: z.string(),
        }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/company/insights': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/company/invite-teammates': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/connect-domain': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/contest/new': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/contra-referral': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.object({
            action: z.string(),
            origin: z.string(),
            redirectTo: z.string(),
            referralExperimentNid: z.string().optional(),
            referrerUsername: z.string(),
        }),
    }),
    '/contracts': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/discover/independents': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            expert: z.boolean().optional().default(false),
            expertPartnership: DiscoverExpertsRouteBase.optional(),
            languages: z.array(z.string()).optional(),
            list: z.string().optional(),
            location: z.string().optional(),
            maxRate: z.number().optional(),
            minRate: z.number().optional(),
            openAdvancedFiltersSubscriptionModal: z
                .boolean()
                .optional()
                .default(false),
            openAdvancedSearchUpgradeModal: z.boolean().optional().default(false),
            openSubscriptionModal: z.boolean().optional().default(false),
            placeId: z.string().optional(),
            preview: z.string().optional(),
            profileId: z.string().optional(),
            query: z.string().optional(),
            quickResponder: z.boolean().optional().default(false),
            roleIds: z.array(z.string()).optional(),
            selectedBillingCycle: z.enum(['MONTHLY', 'YEARLY']).optional(),
            selectedPlan: ClientWorkspacePlanNid.optional(),
            showAd: z.boolean().optional().default(false),
            sortBy: z.enum(['RELEVANCE', 'TOP_RATED', 'MOST_EARNED']).optional(),
            startInquiry: z.boolean().optional().default(false),
            suppressedKeywords: z.array(z.string()).optional(),
            suppressedLanguages: z.array(z.string()).optional(),
            suppressedPlaceId: z.string().optional(),
            suppressedRoleIds: z.array(z.string()).optional(),
            suppressedToolIds: z.array(z.string()).optional(),
            timezoneExtendedOffset: z.number().optional(),
            timezonePrimaryOffset: z.number().optional(),
            toolIds: z.array(z.string()).optional(),
            topIndependent: z.boolean().optional().default(false),
            // this is only used for testing
            userAccountIds: z.array(z.string()).optional(),
        })
            .optional(),
    }),
    '/discover/jobs': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            justApplied: z.boolean().optional().default(false),
            justSubscribedViaJobFeedAccessRequest: z
                .boolean()
                .optional()
                .default(false),
            refresh: z.boolean().optional().default(false),
            sort: z.enum(['MOST_RELEVANT', 'NEWEST']).optional(),
        })
            .optional(),
    }),
    '/discover/jobs/applied': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            justApplied: z.boolean().optional().default(false),
            showAppliedToContestModal: z.boolean().optional().default(false),
            sort: z.enum(['MOST_RELEVANT', 'NEWEST']).optional(),
            submittedEntryJobSlug: z.string().optional(),
        })
            .optional(),
    }),
    '/discover/lists/@id': z.object({
        args: z.object({ id: z.string() }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/discover/projects': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            expertPartnership: DiscoverExpertsRouteBase.optional(),
            list: z.string().optional(),
            // this is only used for testing
            portfolioProjectIds: z.array(z.string()).optional(),
            query: z.string().optional(),
            roleIds: z.array(z.string()).optional(),
            toolIds: z.array(z.string()).optional(),
        })
            .optional(),
    }),
    '/discover/projects/@filter': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/discover/projects/@filter/@facet': z.object({
        args: z.object({ slug: z.string() }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/discover/services': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            expertPartnership: DiscoverExpertsRouteBase.optional(),
            list: z.string().optional(),
            // deprecated
            outcomes: z.array(z.string()).optional(),
            // this is only used for testing
            productizedServiceIds: z.array(z.string()).optional(),
            query: z.string().optional(),
            roleIds: z.array(z.string()).optional(),
            // deprecated
            title: z.string().optional(),
            toolIds: z.array(z.string()).optional(),
        })
            .optional(),
    }),
    '/discover/services/@filter': z.object({
        args: z.object({ filter: z.string() }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/discover/services/@filter/@facet': z.object({
        args: z.object({ facet: z.string(), filter: z.string() }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/documents/@documentId/request-signature': z.object({
        args: z.object({ documentId: z.string() }),
        search: z.object({
            contactId: z.string(),
            requestMessage: z.string(),
        }),
    }),
    '/documents/@documentId/sign': z.object({
        args: z.object({ documentId: z.string() }),
        search: z.object({
            documentUUID: z.string().optional(),
        }),
    }),
    '/documents/email-preview': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.object({
            documentDescription: z.string(),
            documentTitle: z.string(),
            message: z.string(),
        }),
    }),
    '/edit-profile': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/get-started': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            clientGoalType: z
                .enum(['HireFreelancers', 'ManageProjects'])
                .optional(),
            hustleFundDiscount: z.boolean().optional(),
            inviterFirstName: z.string().optional(),
            inviterUsername: z.string().optional(),
            isReferral: z.boolean().optional().default(false),
            networkInviteSlug: z.string().optional(),
            onboardingSource: OnboardingSources.optional(),
            redirectTo: z.string().optional(),
            userType: UserTypeRouteBase.optional(),
        })
            .optional(),
    }),
    '/help': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/independent/analytics': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            refresh: z.boolean().optional().default(false),
            shareProfile: z.boolean().optional().default(false),
        })
            .optional(),
    }),
    '/independent/analytics/viewers/impressions': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/independent/analytics/viewers/portfolio': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/independent/discovery': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            education: IndependentDiscoverabilityFactorEducationDrawer.optional(),
            skill: z.string().optional(),
            tool: z.string().optional(),
        })
            .optional(),
    }),
    '/independent/home': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            justSubscribedToPro: z.boolean().optional().default(false),
            justSubscribedToProModalOpen: z.boolean().optional().default(false),
            openSubscriptionModal: z.boolean().optional().default(false),
            refresh: z.boolean().optional().default(false),
            shareProfile: z.boolean().optional().default(false),
            upgradeToPro: z.boolean().optional().default(false),
        })
            .optional(),
    }),
    '/independent/home/viewers/impressions': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/independent/home/viewers/portfolio': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/independent/invoices/new': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            collaborator: z.string().optional(),
            emailAddress: z.string().optional(),
            guestInvoiceId: z.string().optional(),
            project: z.string().optional(),
            redeem: z.boolean().optional().default(false),
        })
            .optional(),
    }),
    '/independent/projects/@paidProjectId/create-invoice': z.object({
        args: z.object({
            paidProjectId: z.string(),
        }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/independent/request-recommendation': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/invite/@username': z.object({
        args: z.object({ username: z.string() }),
        search: z
            .object({
            action: z.string().optional(),
            referralExperimentNid: z.string().optional(),
            referralIntentId: z.string().optional(),
            userType: UserTypeRouteBase.optional(),
        })
            .optional(),
    }),
    '/invoices/invite/@authToken': z.object({
        args: z.object({
            authToken: z.string(),
        }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/jobs/new': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            autoRecovery: z.boolean().optional().default(false),
            outcome: z.string().optional(),
            parameters: z.string().optional(),
        })
            .optional(),
    }),
    '/lists/@slug': z.object({
        args: z.object({ slug: z.string() }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/lists/projects/@id': z.object({
        args: z.object({ id: z.string() }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/lists/saved': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/log-in': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            contraProReferral: z.boolean().optional().default(false),
            contraProWaitlist: z.boolean().optional().default(false),
            demoMeetingScheduledFor: z.string().optional(),
            emailAddress: z.string().email().optional(),
            expedited: z.boolean().optional().default(false),
            inviteToNetwork: z.boolean().optional().default(false),
            networkInviteSlug: z.string().optional(),
            organizationInviteToken: z.string().optional(),
            redirectTo: z.string().optional(),
            thirdPartyAuth: z.literal('google').optional(),
            userType: UserTypeRouteBase.optional(),
        })
            .optional(),
    }),
    '/log-out': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            redirectTo: z.string().optional(),
        })
            .optional(),
    }),
    '/me': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            addBio: z.boolean().optional().default(false),
            shareProfile: z.boolean().optional().default(false),
        })
            .optional(),
    }),
    '/messages': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            channelId: z.string(),
        })
            .optional(),
    }),
    '/messages/@id': z.object({
        args: z.object({ id: z.string() }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/messages/archived': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/my-work/@paidProjectId': z.object({
        args: z.object({ paidProjectId: z.string() }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/network': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            addedBy: z.array(z.string()).optional(),
            archived: z.string().optional(),
            languages: z.array(z.string()).optional(),
            locations: z.array(z.string()).optional(),
            maxHourlyRate: z.number().optional(),
            minHourlyRate: z.number().optional(),
            page: z.number().optional().default(1),
            projects: z.array(z.string()).optional(),
            refresh: z.boolean().optional().default(false),
            roles: z.array(z.string()).optional(),
            search: z.string().optional(),
            selectedUserList: z.string().optional(),
            selectedView: z.string().optional(),
            sortBy: z.enum(['ACTIVE', 'NEWEST', 'OLDEST', 'A-Z', 'Z-A']).optional(),
            tags: z.array(z.string()).optional(),
            tools: z.array(z.string()).optional(),
            workedWith: z.string().optional(),
        })
            .optional(),
    }),
    '/network-invite/@slug': z.object({
        args: z.object({ slug: z.string() }),
        search: z
            .object({
            emailAddress: z.string().email().optional(),
        })
            .optional(),
    }),
    '/network/add-people': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/network/contact/@contactId': z.object({
        args: z.object({ contactId: z.string() }),
        search: z
            .object({
            initialTab: z
                .enum([
                'invoices',
                'notes',
                'projects',
                'recommendations',
                'documents',
                'compliance',
            ])
                .optional(),
        })
            .optional(),
    }),
    '/network/contact/@contactId/edit': z.object({
        args: z.object({ contactId: z.string() }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/network/invite': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            redirectToMessages: z.boolean().optional().default(false),
            users: z.string().optional(),
        })
            .optional(),
    }),
    '/network/invite/preview': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            message: z.string(),
        })
            .optional(),
    }),
    '/network/messages': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            conversationsType: z.enum(['NETWORK', 'REQUEST']).optional(),
            jobId: z.string().optional(),
            networkInviteSlug: z.string().optional(),
        })
            .optional(),
    }),
    '/network/messages/@conversationId': z.object({
        args: z.object({ conversationId: z.string() }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/notifications': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.object({ notificationId: z.string().optional() }).optional(),
    }),
    '/o/@slug': z.object({
        args: z.object({ slug: z.string() }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/openai': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            query: z.string().optional(),
        })
            .optional(),
    }),
    '/opportunities': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/opportunity/@slug': z.object({
        args: z.object({ slug: z.string() }),
        search: z
            .object({
            chatConversationId: z.string().optional(),
            conversationId: z.string().optional(),
            showApply: z.boolean().optional().default(false),
            showBoostApplicationModal: z.boolean().optional().default(false),
            showBoostJobModal: z.boolean().optional().default(false),
            showDismiss: z.boolean().optional().default(false),
            showInvite: z.boolean().optional().default(false),
            showSocialShareModal: z.boolean().optional().default(false),
            viewAsPublic: z.boolean().optional().default(false),
        })
            .optional(),
    }),
    '/opportunity/@slug/admin': z.object({
        args: z.object({ slug: z.string() }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/opportunity/@slug/applicants': z.object({
        args: z.object({ slug: z.string() }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/opportunity/@slug/apply': z.object({
        args: z.object({ slug: z.string() }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/opportunity/@slug/candidates': z.object({
        args: z.object({ slug: z.string() }),
        search: z
            .object({
            candidateId: z.string().optional(),
            candidateSource: z
                .enum(['CONTRA_PICK', 'CONTRA_VERIFIED_USER'])
                .optional(),
            page: z.number().optional().default(1),
            sortBy: z.enum(['CREATED_AT_DESC', 'CREATED_AT_ASC']).optional(),
            statuses: z
                .array(z.enum([
                'CONTEST_SELECTED_AS_CONTESTANT',
                'CONTEST_SUBMITTED_ENTRY',
                'CONTEST_WINNER',
                'IN_REVIEW',
                'MESSAGED',
                'NEW',
                'NOT_A_FIT',
                'PENDING_INQUIRY_APPROVAL',
                'SHORTLISTED',
                'EXPERT',
            ]))
                .optional(),
        })
            .optional(),
    }),
    '/opportunity/@slug/candidates/@candidateId': z.object({
        args: z.object({ candidateId: z.string(), slug: z.string() }),
        search: z
            .object({
            backTo: z.string().optional(),
            statuses: z
                .array(z.enum([
                'CONTEST_SELECTED_AS_CONTESTANT',
                'CONTEST_SUBMITTED_ENTRY',
                'CONTEST_WINNER',
                'IN_REVIEW',
                'MESSAGED',
                'NEW',
                'NOT_A_FIT',
                'PENDING_INQUIRY_APPROVAL',
                'SHORTLISTED',
                'EXPERT',
            ]))
                .optional(),
        })
            .optional(),
    }),
    '/opportunity/@slug/contestants': z.object({
        args: z.object({ slug: z.string() }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/opportunity/@slug/edit': z.object({
        args: z.object({ slug: z.string() }),
        search: z
            .object({
            newAnonymousJob: z.boolean().optional().default(false),
            newJob: z.boolean().optional().default(false),
            openPublishModal: z.boolean().optional().default(false),
            showRevealAnimation: z.boolean().optional().default(false),
        })
            .optional(),
    }),
    '/opportunity/@slug/experts': z.object({
        args: z.object({ slug: z.string() }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/opportunity/@slug/hires': z.object({
        args: z.object({ slug: z.string() }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/opportunity/@slug/messaged': z.object({
        args: z.object({ slug: z.string() }),
        search: z
            .object({
            candidateId: z.string().optional(),
            page: z.number().optional().default(1),
            sortBy: z.enum(['CREATED_AT_DESC', 'CREATED_AT_ASC']).optional(),
        })
            .optional(),
    }),
    '/opportunity/@slug/new': z.object({
        args: z.object({ slug: z.string() }),
        search: z
            .object({
            newAnonymousJob: z.boolean().optional().default(false),
            openPublishModal: z.boolean().optional().default(false),
        })
            .optional(),
    }),
    '/opportunity/@slug/not-a-fit': z.object({
        args: z.object({ slug: z.string() }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/opportunity/@slug/shortlisted': z.object({
        args: z.object({ slug: z.string() }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/opportunity/@slug/submissions': z.object({
        args: z.object({ slug: z.string() }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/opportunity/@slug/submit': z.object({
        args: z.object({ slug: z.string() }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/opportunity/@slug/winners': z.object({
        args: z.object({ slug: z.string() }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/opportunity/draft/@llmRequestId/edit': z.object({
        args: z.object({ llmRequestId: z.string() }),
        search: z
            .object({
            showRevealAnimation: z.boolean().optional().default(false),
        })
            .optional(),
    }),
    '/opportunity/draft/@llmRequestId/new': z.object({
        args: z.object({ llmRequestId: z.string() }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/opportunity/new': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/p/@slug': z.object({
        args: z.object({ slug: z.string() }),
        search: z
            .object({
            justPublished: z.boolean().optional().default(false),
            justUpdated: z.boolean().optional().default(false),
            magic: z.boolean().optional().default(false),
        })
            .optional(),
    }),
    '/p/@slug/edit': z.object({
        args: z.object({ slug: z.string() }),
        search: z
            .object({
            editProjectDetails: z.boolean().optional().default(false),
            fromRequestId: z.string().optional(),
            isProjectCreatedFromLink: z.boolean().optional().default(false),
            justCreated: z.boolean().optional().default(false),
            projectTemplateId: z.string().optional(),
        })
            .optional(),
    }),
    '/payments': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/payments/create-account': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/payments/onboard-account': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/payments/send-direct-payment': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            contestSlug: z.string().optional(),
        })
            .optional(),
    }),
    '/payments/stripeConnectOnboardingRedirect': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/portfolio': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            openCustomizationSidebarScreen: z
                .enum(['logo', 'styles', 'main'])
                .optional(),
            referralIntentId: z.string().optional(),
            shareProfile: z.boolean().optional().default(false),
            upgradeNow: z.boolean().optional().default(false),
        })
            .optional(),
    }),
    '/pro-guide': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/project/magic': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            editProject: z.string().optional(),
            editProjectId: z.string().optional(),
            fromRequestId: z.string().optional(),
            paidProjectId: z.string().optional(),
        })
            .optional(),
    }),
    '/project/new': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            fromRequestId: z.string().optional(),
            importContent: z.boolean().optional().default(false),
            magic: z.boolean().optional().default(false),
            title: z.string().optional(),
        })
            .optional(),
    }),
    '/projects': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/proposal/new': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            collaborator: z.string().optional(),
            email: z.string().optional(),
            firstName: z.string().optional(),
            jobId: z.string().optional(),
            lastName: z.string().optional(),
            newCollaborator: z.boolean().optional().default(false),
            users: z.string().optional(),
        })
            .optional(),
    }),
    '/proposals/@termDraftId': z.object({
        args: z.object({ termDraftId: z.string() }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/redeem/@code': z.object({
        args: z.object({ code: z.string() }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/refer-and-earn': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/refer-and-earn-email-preview': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/request-a-demo': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/request-membership': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.object({
            email: z.string().nullable(),
            firstName: z.string().nullable(),
            joinMembershipToday: z.boolean().optional().default(false),
            lastName: z.string().nullable(),
            redirectTo: z.string().optional(),
        }),
    }),
    '/s/@slug': z.object({
        args: z.object({ slug: z.string() }),
        search: z
            .object({
            from: z.string().optional(),
            justPublished: z.boolean().optional().default(false),
            justUpdated: z.boolean().optional().default(false),
            preview: z.boolean().optional().default(false),
            tab: z.enum(['description', 'about', 'recommendations']).optional(),
        })
            .optional(),
    }),
    '/s/@slug/checkout': z.object({
        args: z.object({ slug: z.string() }),
        search: z
            .object({
            tab: z.enum(['description', 'about', 'recommendations']).optional(),
        })
            .optional(),
    }),
    '/s/@slug/edit': z.object({
        args: z.object({ slug: z.string() }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/s/@slug/embed': z.object({
        args: z.object({
            slug: z.string(),
        }),
        search: z
            .object({
            structure: z.enum(['horizontal', 'vertical']).optional(),
        })
            .optional(),
    }),
    '/s/@slug/purchased/@purchaseId': z.object({
        args: z.object({
            purchaseId: z.string(),
            slug: z.string(),
        }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/service/new': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/settings': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            highlight: z.string().optional(),
            justConnectedDomain: z.boolean().optional().default(false),
        })
            .optional(),
    }),
    '/settings/branding': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            animated: z.boolean().optional().default(false),
            height: z.number().optional(),
            justSubscribedToProCustomBrandingModalOpen: z
                .boolean()
                .optional()
                .default(false),
            logoPreviewUrl: z.string().optional(),
            openBrandCustomizationModal: z.boolean().optional().default(false),
            originalFormat: z.string().optional(),
            sizeBytes: z.number().optional(),
            uid: z.string().optional(),
            width: z.number().optional(),
        })
            .optional(),
    }),
    '/settings/email-preferences': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            email: z.string().email().optional(),
            group: z
                .enum([
                'marketing',
                'payments',
                'support',
                'messages',
                'jobPosting',
                'profileSuggestions',
                'projectManagement',
                'availabilityReminders',
                'contraPro',
            ])
                .optional(),
            id: z.string().optional(),
        })
            .optional(),
    }),
    '/settings/hire-me': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/settings/payment-methods': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/settings/rewards': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/settings/subscription': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/settings/workspace/business-information': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/settings/workspace/members': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/settings/workspace/subscription': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            highlightedPlan: ClientWorkspacePlanNid.optional(),
            openSubscriptionModal: z.boolean().optional().default(false),
            selectedBillingCycle: z.enum(['MONTHLY', 'YEARLY']).optional(),
            selectedPlan: ClientWorkspacePlanNid.optional(),
        })
            .optional(),
    }),
    '/sign-up': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            contraProReferral: z.boolean().optional().default(false),
            contraProWaitlist: z.boolean().optional().default(false),
            emailAddress: z.string().email().optional(),
            expedited: z.boolean().optional().default(false),
            hustleFundDiscount: z.boolean().optional(),
            inviterFirstName: z.string().optional(),
            inviterOrganizationSlug: z.string().optional(),
            inviterUsername: z.string().optional(),
            inviteToNetwork: z.boolean().optional().default(false),
            isGuestCheckout: z.boolean().optional().default(false),
            isReferral: z.boolean().optional().default(false),
            networkInviteSlug: z.string().optional(),
            onboardingSource: OnboardingSources.optional(),
            organizationInviteToken: z.string().optional(),
            redirectTo: z.string().optional(),
            referralIntentId: z.string().optional(),
            requestDemoSubmitted: z.boolean().optional().default(false),
            showOpportunityHeader: z.boolean().optional().default(false),
            showWhySignUp: z.boolean().optional().default(false),
            skipHowDidYouHearAboutUs: z.boolean().optional().default(false),
            skipUserTypeSelection: z.boolean().optional().default(false),
            thirdPartyAuth: z.literal('google').optional(),
            userType: UserTypeRouteBase.optional(),
            utm_campaign: z.string().optional(),
            utm_medium: z.string().optional(),
            utm_source: z.string().optional(),
        })
            .optional(),
    }),
    '/stripe-3ds-return': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/subscribe': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            hustleFundDiscount: z.boolean().optional(),
            selectedBillingCycle: z.enum(['MONTHLY', 'YEARLY']).default('YEARLY'),
            selectedPlan: ClientWorkspacePlanNid.optional(),
        })
            .optional(),
    }),
    '/transaction/@id': z.object({
        args: z.object({ id: z.string() }),
        search: z.record(z.string(), z.never()).optional(),
    }),
    '/wallet': z.object({
        args: z.record(z.string(), z.never()).optional(),
        search: z
            .object({
            addPayoutMethod: z.boolean(),
        })
            .optional(),
    }),
    '/work/invite/@token': z.object({
        args: z.object({ token: z.string() }),
        search: z.object({
            paidProjectId: z.string(),
        }),
    }),
};
//# sourceMappingURL=routes.js.map